import React, { useState } from 'react';
import axios from 'axios';
import { UserCircle, Lock, FloppyDisk, CheckCircle, WarningCircle, Eye, EyeSlash } from '@phosphor-icons/react';
import useAuthStore from '../store/authStore';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Banner = ({ msg }) =>
  msg ? (
    <div
      className={`flex items-start gap-2 px-3 py-2 rounded-md text-sm border ${
        msg.type === 'success'
          ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
          : 'bg-red-50 border-red-200 text-red-700'
      }`}
      data-testid={msg.type === 'success' ? 'profile-banner-success' : 'profile-banner-error'}
    >
      {msg.type === 'success' ? (
        <CheckCircle size={18} weight="fill" className="shrink-0 mt-0.5" />
      ) : (
        <WarningCircle size={18} weight="fill" className="shrink-0 mt-0.5" />
      )}
      <span>{msg.text}</span>
    </div>
  ) : null;

const Profile = () => {
  const { user, fetchUser } = useAuthStore();

  // Profile fields
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileMsg, setProfileMsg] = useState(null); // { type: 'success'|'error', text }

  // Password fields
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [passwordMsg, setPasswordMsg] = useState(null);

  const profileChanged =
    (name || '').trim() !== (user?.name || '') ||
    (email || '').trim() !== (user?.email || '') ||
    (phone || '').trim() !== (user?.phone || '');

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    if (!profileChanged) return;
    setSavingProfile(true);
    setProfileMsg(null);
    try {
      await axios.patch(`${API}/auth/me`, {
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
      });
      await fetchUser();
      setProfileMsg({ type: 'success', text: 'Profile updated successfully.' });
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to update profile';
      setProfileMsg({ type: 'error', text: detail });
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordMsg(null);

    if (!currentPassword || !newPassword) {
      setPasswordMsg({ type: 'error', text: 'Please fill in both current and new password.' });
      return;
    }
    if (newPassword.length < 8) {
      setPasswordMsg({ type: 'error', text: 'New password must be at least 8 characters.' });
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordMsg({ type: 'error', text: 'New password and confirmation do not match.' });
      return;
    }

    setSavingPassword(true);
    try {
      await axios.patch(`${API}/auth/me`, {
        current_password: currentPassword,
        new_password: newPassword,
      });
      setPasswordMsg({ type: 'success', text: 'Password changed successfully.' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to change password';
      setPasswordMsg({ type: 'error', text: detail });
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <div className="p-6 lg:p-8 max-w-3xl mx-auto" data-testid="profile-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-full bg-[#E2F3F0] flex items-center justify-center text-[#00a69c]">
          <UserCircle size={28} weight="fill" />
        </div>
        <div>
          <h1 className="app-page-title" style={{ fontFamily: 'Outfit' }}>
            My Profile
          </h1>
          <p className="text-sm text-[#6D8E87]">
            Update your personal details and password.
          </p>
        </div>
      </div>

      {/* Read-only meta */}
      <div className="bg-white rounded-xl border border-[#D5E8E4] p-5 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
          <div>
            <p className="text-xs text-[#6D8E87] mb-1">Role</p>
            <p className="font-medium text-[#052A23]" data-testid="profile-role">{user?.role || '—'}</p>
          </div>
          <div>
            <p className="text-xs text-[#6D8E87] mb-1">Region</p>
            <p className="font-medium text-[#052A23]" data-testid="profile-region">{user?.region || '—'}</p>
          </div>
          <div>
            <p className="text-xs text-[#6D8E87] mb-1">Employee ID</p>
            <p className="font-medium text-[#052A23]" data-testid="profile-employee-id">{user?.employee_id || '—'}</p>
          </div>
        </div>
      </div>

      {/* Personal details */}
      <form
        onSubmit={handleSaveProfile}
        className="bg-white rounded-xl border border-[#D5E8E4] p-5 mb-6 space-y-4"
        data-testid="profile-details-form"
      >
        <h2 className="text-lg font-semibold text-[#052A23]">Personal details</h2>

        <Banner msg={profileMsg} />

        <div>
          <label className="block text-sm font-medium text-[#3B5A54] mb-1">Full name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full px-3 py-2 border border-[#D5E8E4] rounded-md focus:outline-none focus:ring-2 focus:ring-[#00a69c]/30 focus:border-[#00a69c] text-sm"
            data-testid="profile-name-input"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-[#3B5A54] mb-1">Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-3 py-2 border border-[#D5E8E4] rounded-md focus:outline-none focus:ring-2 focus:ring-[#00a69c]/30 focus:border-[#00a69c] text-sm"
            data-testid="profile-email-input"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-[#3B5A54] mb-1">Phone</label>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="w-full px-3 py-2 border border-[#D5E8E4] rounded-md focus:outline-none focus:ring-2 focus:ring-[#00a69c]/30 focus:border-[#00a69c] text-sm"
            data-testid="profile-phone-input"
          />
        </div>

        <div className="flex justify-end pt-2">
          <button
            type="submit"
            disabled={savingProfile || !profileChanged}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#00a69c] text-white rounded-md text-sm font-medium hover:bg-[#008c84] transition disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="profile-save-btn"
          >
            <FloppyDisk size={16} weight="bold" />
            {savingProfile ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </form>

      {/* Change password */}
      <form
        onSubmit={handleChangePassword}
        className="bg-white rounded-xl border border-[#D5E8E4] p-5 space-y-4"
        data-testid="profile-password-form"
      >
        <div className="flex items-center gap-2">
          <Lock size={18} className="text-[#00a69c]" />
          <h2 className="text-lg font-semibold text-[#052A23]">Change password</h2>
        </div>

        <Banner msg={passwordMsg} />

        <div>
          <label className="block text-sm font-medium text-[#3B5A54] mb-1">Current password</label>
          <div className="relative">
            <input
              type={showCurrent ? 'text' : 'password'}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="w-full px-3 py-2 pr-10 border border-[#D5E8E4] rounded-md focus:outline-none focus:ring-2 focus:ring-[#00a69c]/30 focus:border-[#00a69c] text-sm"
              data-testid="profile-current-password-input"
              autoComplete="current-password"
            />
            <button
              type="button"
              onClick={() => setShowCurrent((s) => !s)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-[#6D8E87] hover:text-[#00a69c]"
              aria-label="Toggle current password visibility"
            >
              {showCurrent ? <EyeSlash size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-[#3B5A54] mb-1">New password</label>
          <div className="relative">
            <input
              type={showNew ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full px-3 py-2 pr-10 border border-[#D5E8E4] rounded-md focus:outline-none focus:ring-2 focus:ring-[#00a69c]/30 focus:border-[#00a69c] text-sm"
              data-testid="profile-new-password-input"
              autoComplete="new-password"
            />
            <button
              type="button"
              onClick={() => setShowNew((s) => !s)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-[#6D8E87] hover:text-[#00a69c]"
              aria-label="Toggle new password visibility"
            >
              {showNew ? <EyeSlash size={16} /> : <Eye size={16} />}
            </button>
          </div>
          <p className="text-xs text-[#6D8E87] mt-1">At least 8 characters.</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-[#3B5A54] mb-1">Confirm new password</label>
          <input
            type={showNew ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="w-full px-3 py-2 border border-[#D5E8E4] rounded-md focus:outline-none focus:ring-2 focus:ring-[#00a69c]/30 focus:border-[#00a69c] text-sm"
            data-testid="profile-confirm-password-input"
            autoComplete="new-password"
          />
        </div>

        <div className="flex justify-end pt-2">
          <button
            type="submit"
            disabled={savingPassword}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#00a69c] text-white rounded-md text-sm font-medium hover:bg-[#008c84] transition disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="profile-change-password-btn"
          >
            <Lock size={16} weight="bold" />
            {savingPassword ? 'Updating…' : 'Update password'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default Profile;
